import { useCallback, useRef } from 'react';

interface UsePressAndHoldOptions {
  /** Hold duration before the press qualifies. */
  delayMs?: number;
  /** Movement (px) that cancels the press as a scroll/drag. */
  moveTolerance?: number;
}

interface PressAndHoldHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onClickCapture: (e: React.MouseEvent) => void;
}

/**
 * Press-and-hold gesture for both mouse and touch (unified via Pointer Events).
 * The press *qualifies* once the primary pointer is held for `delayMs` without
 * moving more than `moveTolerance` px, and fires `onPressAndHold(x, y)` on
 * release. Firing on release (rather than mid-hold) is deliberate: the thing it
 * opens — a scrollable preview overlay — must survive the pointer-up to be
 * scrollable, and opening on release means the just-mounted overlay can't
 * swallow the release as a stray dismiss.
 *
 * After it fires, the returned `onClickCapture` swallows the click that the
 * release produces, so a press-and-hold never also triggers the element's
 * normal `onClick`. In the chat channel list that click selects the channel —
 * which marks it read — and the preview's whole promise is to leave it unread.
 *
 * The press is cancelled by movement (a scroll/drag), leaving the element, a
 * non-primary button, or a cancelled pointer.
 */
export function usePressAndHold(
  onPressAndHold: (x: number, y: number) => void,
  { delayMs = 400, moveTolerance = 10 }: UsePressAndHoldOptions = {},
): PressAndHoldHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const qualifiedRef = useRef(false); // held long enough — open on release
  const firedRef = useRef(false); // opened — swallow the trailing click

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
    qualifiedRef.current = false;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Primary button only (0 for mouse / touch / pen). Right- and
      // middle-click must not shadow the context menu or start a hold.
      if (e.button !== 0) {
        reset();
        return;
      }
      firedRef.current = false;
      qualifiedRef.current = false;
      startRef.current = { x: e.clientX, y: e.clientY };
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        qualifiedRef.current = true;
      }, delayMs);
    },
    [reset, delayMs],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const s = startRef.current;
      if (!s) return;
      if (Math.abs(e.clientX - s.x) > moveTolerance || Math.abs(e.clientY - s.y) > moveTolerance) {
        reset();
      }
    },
    [reset, moveTolerance],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (qualifiedRef.current) {
        firedRef.current = true;
        onPressAndHold(e.clientX, e.clientY);
      }
      reset();
    },
    [onPressAndHold, reset],
  );

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (firedRef.current) {
      firedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave: reset,
    onPointerCancel: reset,
    onClickCapture,
  };
}
